import aiLogger from './ai-logger';
import { Component } from "./component";

interface ChatFile {
    message_id: number;
    index: number;
    name: string;
    mime_type: string;
    size: number;
    created_at: string;
    url: string;
}

interface ChatChange {
    id: number;
    tool: string;
    action: string | null;
    title: string;
    url: string | null;
    user_name: string | null;
    created_at: string;
}

const changeToolLabels: Record<string, string> = {
    azure_devops: 'Azure DevOps',
    create_documentation: 'Documentação',
    gitlab: 'GitLab',
};

export class ChatFilesSidebar extends Component {
    protected files: ChatFile[] = [];
    protected changes: ChatChange[] = [];
    protected activeConversationId: number | null = null;
    protected isOpen: boolean = false;

    protected listEl!: HTMLDivElement;
    protected emptyEl!: HTMLDivElement;
    protected changesSectionEl!: HTMLDivElement;
    protected changesListEl!: HTMLDivElement;
    protected changesCountEl!: HTMLSpanElement;
    protected toggleButton!: HTMLButtonElement;
    protected sidebarOverlay!: HTMLDivElement;

    // Preview modal refs
    protected previewModal!: HTMLDivElement;
    protected previewBackdrop!: HTMLDivElement;
    protected previewTitle!: HTMLSpanElement;
    protected previewDownload!: HTMLAnchorElement;
    protected previewClose!: HTMLButtonElement;
    protected previewBody!: HTMLDivElement;

    setup() {
        this.files = [];
        this.activeConversationId = null;
        this.isOpen = false;

        // Refs (dentro do componente)
        this.listEl      = this.$refs.list        as HTMLDivElement;
        this.emptyEl     = this.$refs.empty       as HTMLDivElement;
        this.changesSectionEl = this.$refs.changesSection as HTMLDivElement;
        this.changesListEl    = this.$refs.changesList    as HTMLDivElement;
        this.changesCountEl   = this.$refs.changesCount   as HTMLSpanElement;

        // Preview modal refs (fora do componente — usa getElementById pois
        // transform no aside cria containing block que quebra position:fixed)
        this.previewModal    = document.getElementById('file-preview-modal')    as HTMLDivElement;
        this.previewBackdrop = document.getElementById('file-preview-backdrop') as HTMLDivElement;
        this.previewTitle    = document.getElementById('file-preview-title')    as HTMLSpanElement;
        this.previewDownload = document.getElementById('file-preview-download') as HTMLAnchorElement;
        this.previewClose    = document.getElementById('file-preview-close')    as HTMLButtonElement;
        this.previewBody     = document.getElementById('file-preview-body')     as HTMLDivElement;

        // Toggle mobile (fica dentro do aside, posicionado à esquerda visualmente)
        this.toggleButton   = this.$el.querySelector('.files-sidebar-toggle-mobile') as HTMLButtonElement;
        // Overlay fica fora do aside
        this.sidebarOverlay = document.querySelector('.files-sidebar-overlay') as HTMLDivElement;

        if (!this.listEl || !this.emptyEl) {
            aiLogger.error('ChatFilesSidebar: refs list/empty não encontrados');
            return;
        }

        // Evento disparado pelo ChatSidebar quando uma conversa é selecionada.
        // O nome DOM: chat-sidebar-conversation-selected (componente=chat-sidebar, evento=conversation-selected)
        document.addEventListener('chat-sidebar-conversation-selected', (e: Event) => {
            const detail = (e as CustomEvent<{id: number; from: unknown}>).detail;
            if (detail && detail.id) {
                this.activeConversationId = detail.id;
                this.loadFiles(detail.id);
                this.loadChanges(detail.id);
            }
        });

        // A IA pode ter criado/alterado cards e documentos durante o stream.
        document.addEventListener('chat-stream-finished', (e: Event) => {
            const detail = (e as CustomEvent<{conversationId: number | null}>).detail;
            if (detail && detail.conversationId) {
                this.activeConversationId = detail.conversationId;
                this.loadChanges(detail.conversationId);
            }
        });

        // Evento disparado pelo AiChat quando o upload é confirmado pelo servidor.
        // Recebe os arquivos reais com URLs de download/preview.
        // Remove itens otimistas e renderiza os confirmados (ou faz reload completo).
        document.addEventListener('chat-files-uploaded', (e: Event) => {
            const detail = (e as CustomEvent<{conversationId: number; files?: ChatFile[]}>).detail;
            if (!detail || !detail.conversationId) return;

            this.activeConversationId = detail.conversationId;
            this.clearOptimisticItems();

            if (detail.files && detail.files.length > 0) {
                this.prependFiles(detail.files);
            } else {
                this.loadFilesSilent(detail.conversationId);
            }
        });

        // Evento disparado NO MOMENTO do clique em enviar (antes do upload completar).
        // Renderiza itens otimistas com dados do cache local (File objects do browser).
        document.addEventListener('chat-files-uploading', (e: Event) => {
            const detail = (e as CustomEvent<{conversationId: number | null; files?: {name: string; size: number; mime_type: string}[]}>).detail;
            if (detail && detail.conversationId) {
                this.activeConversationId = detail.conversationId;
            }
            if (detail?.files && detail.files.length > 0) {
                this.showOptimisticFiles(detail.files);
            }
        });

        // Evento disparado em caso de erro ou abort.
        // Se a mensagem já foi persistida (ChatMessage::create roda ANTES do streaming),
        // recarrega a lista para mostrar o arquivo salvo. Caso contrário, limpa os otimistas.
        document.addEventListener('chat-files-upload-failed', (e: Event) => {
            this.clearOptimisticItems();

            if (this.activeConversationId) {
                // Tenta recarregar — se o arquivo foi salvo, ele aparece; se não, a lista fica limpa
                this.loadFilesSilent(this.activeConversationId);
            } else if (this.listEl && this.listEl.children.length === 0) {
                this.showEmpty();
            }
        });

        document.addEventListener('chat-sidebar-conversation-deleted', (e: Event) => {
            const detail = (e as CustomEvent<{id: number}>).detail;
            if (detail && detail.id === this.activeConversationId) {
                this.activeConversationId = null;
                this.files = [];
                if (this.listEl) this.listEl.innerHTML = '';
                this.showEmpty();
                this.clearChanges();
            }
        });

        // Quando uma nova conversa é criada pelo sendMessage (chat.ts),
        // limpa arquivos da conversa anterior.
        document.addEventListener('chat-conversation-created', (e: Event) => {
            const detail = (e as CustomEvent<{conversationId: number}>).detail;
            if (detail && detail.conversationId) {
                this.activeConversationId = detail.conversationId;
                this.files = [];
                if (this.listEl) this.listEl.innerHTML = '';
                this.showEmpty();
                this.clearChanges();
            }
        });

        // Mobile toggle
        if (this.toggleButton) {
            this.toggleButton.addEventListener('click', () => this.toggleSidebar());
        }

        // Fechar ao clicar no overlay
        if (this.sidebarOverlay) {
            this.sidebarOverlay.addEventListener('click', () => this.closeSidebar());
        }

        // Preview: fechar ao clicar no botão X
        if (this.previewClose) {
            this.previewClose.addEventListener('click', () => this.closePreview());
        }

        // Preview: fechar ao clicar no backdrop
        if (this.previewBackdrop) {
            this.previewBackdrop.addEventListener('click', () => this.closePreview());
        }

        // Fechar modal com ESC
        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.previewModal && !this.previewModal.classList.contains('hidden')) {
                this.closePreview();
            }
        });
    }

    // ============================
    // API
    // ============================
    private getGuestSessionHeader(): Record<string, string> {
        for (const storage of [localStorage, sessionStorage]) {
            try {
                const id = storage.getItem('athena_guest_session_id');
                if (id) return { 'X-Chat-Session-Id': id };
            } catch { /* blocked */ }
        }
        return {};
    }

    protected async loadFiles(conversationId: number): Promise<void> {
        this.showLoadingSpinner();

        try {
            const resp = await fetch(`/chat/conversations/${conversationId}/files`, {
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...this.getGuestSessionHeader() },
                credentials: 'same-origin',
            });

            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

            const data: { files: ChatFile[] } = await resp.json();
            this.files = data.files || [];
            this.renderFileList();
        } catch (err) {
            aiLogger.error('ChatFilesSidebar: erro ao carregar arquivos', err);
            this.showEmpty();
        }
    }

    /**
     * Recarrega a lista completa silenciosamente (sem skeleton, sem fade).
     * Usado como fallback quando chat-files-updated não traz os dados dos arquivos.
     */
    protected async loadFilesSilent(conversationId: number): Promise<void> {
        try {
            const resp = await fetch(`/chat/conversations/${conversationId}/files`, {
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...this.getGuestSessionHeader() },
                credentials: 'same-origin',
            });

            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

            const data: { files: ChatFile[] } = await resp.json();
            this.files = data.files || [];
            this.renderFileList();
        } catch (err) {
            aiLogger.error('ChatFilesSidebar: erro ao recarregar arquivos', err);
        }
    }

    // ============================
    // Alterações da conversa (menu "Alterações")
    // ============================

    protected async loadChanges(conversationId: number): Promise<void> {
        try {
            const resp = await fetch(`/chat/conversations/${conversationId}/changes`, {
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...this.getGuestSessionHeader() },
                credentials: 'same-origin',
            });

            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

            const data: { changes: ChatChange[] } = await resp.json();
            this.changes = data.changes || [];
            this.renderChanges();
        } catch (err) {
            aiLogger.error('ChatFilesSidebar: erro ao carregar alterações', err);
            this.clearChanges();
        }
    }

    protected renderChanges(): void {
        if (!this.changesSectionEl || !this.changesListEl) return;

        if (this.changes.length === 0) {
            this.clearChanges();
            return;
        }

        this.changesSectionEl.classList.remove('hidden');
        if (this.changesCountEl) {
            this.changesCountEl.textContent = `(${this.changes.length})`;
        }

        this.changesListEl.innerHTML = '';
        for (const change of this.changes) {
            this.changesListEl.appendChild(this.createChangeItem(change));
        }
    }

    protected createChangeItem(change: ChatChange): HTMLElement {
        const item = document.createElement('div');
        item.className = 'sidebar-change-item';

        const titleEl: HTMLElement = change.url
            ? document.createElement('a')
            : document.createElement('span');
        titleEl.className = 'change-item-title';
        titleEl.textContent = change.title;
        titleEl.title = change.title;
        if (change.url && titleEl instanceof HTMLAnchorElement) {
            titleEl.href = change.url;
            titleEl.target = '_blank';
            titleEl.rel = 'noopener noreferrer';
        }

        const metaEl = document.createElement('div');
        metaEl.className = 'change-item-meta';
        const parts: string[] = [changeToolLabels[change.tool] || change.tool];
        if (change.created_at) {
            parts.push(new Date(change.created_at).toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            }));
        }
        if (change.user_name) {
            parts.push(change.user_name);
        }
        metaEl.textContent = parts.join(' · ');
        metaEl.title = metaEl.textContent;

        item.appendChild(titleEl);
        item.appendChild(metaEl);
        return item;
    }

    protected clearChanges(): void {
        this.changes = [];
        if (this.changesListEl) this.changesListEl.innerHTML = '';
        if (this.changesCountEl) this.changesCountEl.textContent = '';
        this.changesSectionEl?.classList.add('hidden');
    }

    /**
     * Appenda apenas os novos arquivos no topo da lista, sem tocar nos existentes.
     * Cada item entra com um fade-in suave.
     */
    protected prependFiles(newFiles: ChatFile[]): void {
        if (!this.listEl) return;

        if (this.emptyEl) this.emptyEl.style.display = 'none';
        this.listEl.style.display = '';

        // Adiciona cada novo arquivo ao model local (evita duplicatas por nome)
        for (const f of newFiles) {
            const dup = this.files.find(existing => existing.name === f.name && existing.message_id === f.message_id);
            if (!dup) this.files.unshift(f);
        }

        // Renderiza apenas os novos itens no topo do DOM
        for (const f of [...newFiles].reverse()) {
            const item = this.createFileItem(f);
            item.style.opacity = '0';
            item.style.transition = 'opacity 0.2s ease';
            this.listEl.insertBefore(item, this.listEl.firstChild);
            // Trigger fade-in no próximo frame
            requestAnimationFrame(() => {
                requestAnimationFrame(() => { item.style.opacity = '1'; });
            });
        }
    }

    /**
     * Remove os itens otimistas (marcados com data-optimistic).
     */
    protected clearOptimisticItems(): void {
        if (!this.listEl) return;
        const items = this.listEl.querySelectorAll('[data-optimistic]');
        items.forEach(el => el.remove());
    }

    // ============================
    // Render
    // ============================
    protected renderFileList(): void {
        if (!this.listEl) return;

        if (this.files.length === 0) {
            this.showEmpty();
            return;
        }

        if (this.emptyEl) this.emptyEl.style.display = 'none';
        this.listEl.style.display = '';

        // Fade-out suave dos itens atuais, depois substitui
        this.listEl.style.opacity = '0';
        this.listEl.style.transition = 'opacity 0.15s ease';

        setTimeout(() => {
            if (!this.listEl) return;
            this.listEl.innerHTML = '';
            for (const file of this.files) {
                const item = this.createFileItem(file);
                this.listEl.appendChild(item);
            }
            // Fade-in
            this.listEl.style.opacity = '1';
        }, 150);
    }

    protected createFileItem(file: ChatFile): HTMLElement {
        const item = document.createElement('div');
        item.className = 'sidebar-file-item';

        // Ícone por tipo
        const iconEl = document.createElement('div');
        const {cssClass, label} = this.getFileIconInfo(file.mime_type);
        iconEl.className = `file-item-icon ${cssClass}`;
        iconEl.textContent = label;

        // Conteúdo (nome + meta)
        const contentEl = document.createElement('div');
        contentEl.className = 'file-item-content';

        const nameEl = document.createElement('div');
        nameEl.className = 'file-item-name';
        nameEl.textContent = file.name;
        nameEl.title = file.name;

        const metaEl = document.createElement('div');
        metaEl.className = 'file-item-meta';
        metaEl.textContent = this.formatSize(file.size);

        contentEl.appendChild(nameEl);
        contentEl.appendChild(metaEl);

        // Ações
        const actionsEl = document.createElement('div');
        actionsEl.className = 'file-item-actions';

        // Botão visualizar (só para tipos com preview)
        if (this.isPreviewable(file.mime_type)) {
            const previewBtn = document.createElement('button');
            previewBtn.className = 'btn-icon';
            previewBtn.title = 'Visualizar';
            previewBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5a5 5 0 110-10 5 5 0 010 10zm0-8a3 3 0 100 6 3 3 0 000-6z"/></svg>`;
            previewBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.openPreview(file);
            });
            actionsEl.appendChild(previewBtn);
        }

        // Botão download
        const downloadLink = document.createElement('a');
        downloadLink.className = 'btn-icon';
        downloadLink.title = 'Baixar';
        downloadLink.href = file.url;
        downloadLink.download = file.name;
        downloadLink.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>`;
        actionsEl.appendChild(downloadLink);

        item.appendChild(iconEl);
        item.appendChild(contentEl);
        item.appendChild(actionsEl);

        return item;
    }

    // ============================
    // Preview Modal
    // ============================
    protected openPreview(file: ChatFile): void {
        if (!this.previewModal) return;

        // Título
        if (this.previewTitle) {
            this.previewTitle.textContent = file.name;
        }

        // Link de download
        if (this.previewDownload) {
            this.previewDownload.href = file.url;
            this.previewDownload.download = file.name;
        }

        // Corpo do preview
        if (this.previewBody) {
            this.previewBody.innerHTML = '';

            if (file.mime_type.startsWith('image/')) {
                // Loader enquanto a imagem carrega
                const loader = document.createElement('div');
                loader.className = 'file-preview-loader';
                loader.innerHTML = '<div class="preview-spinner"></div><span>Carregando...</span>';
                this.previewBody.appendChild(loader);

                const img = document.createElement('img');
                img.alt = file.name;
                img.style.display = 'none';
                img.onload = () => {
                    loader.remove();
                    img.style.display = '';
                };
                img.onerror = () => {
                    loader.innerHTML = '<span style="color:var(--color-text-muted)">Erro ao carregar a imagem.</span>';
                };
                this.previewBody.appendChild(img);
                img.src = file.url;

            } else if (file.mime_type === 'application/pdf') {
                // Loader enquanto o PDF carrega
                const loader = document.createElement('div');
                loader.className = 'file-preview-loader';
                loader.innerHTML = '<div class="preview-spinner"></div><span>Carregando...</span>';
                this.previewBody.appendChild(loader);

                const iframe = document.createElement('iframe');
                iframe.title = file.name;
                iframe.style.display = 'none';
                iframe.onload = () => {
                    loader.remove();
                    iframe.style.display = '';
                };
                this.previewBody.appendChild(iframe);
                iframe.src = file.url;

            } else if (file.mime_type.startsWith('text/')) {
                const textLoader = document.createElement('div');
                textLoader.className = 'file-preview-loader';
                textLoader.innerHTML = '<div class="preview-spinner"></div><span>Carregando...</span>';
                this.previewBody.appendChild(textLoader);

                const pre = document.createElement('pre');
                pre.style.display = 'none';
                this.previewBody.appendChild(pre);

                fetch(file.url, { credentials: 'same-origin' })
                    .then(r => r.text())
                    .then(text => { textLoader.remove(); pre.textContent = text; pre.style.display = ''; })
                    .catch(() => { textLoader.remove(); pre.textContent = '(Erro ao carregar o conteúdo do arquivo)'; pre.style.display = ''; });

            } else {
                const unsupported = document.createElement('div');
                unsupported.className = 'file-preview-unsupported';
                unsupported.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                    <p>Pré-visualização não disponível para este tipo de arquivo.</p>
                `;
                this.previewBody.appendChild(unsupported);
            }
        }

        this.previewModal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
    }

    protected closePreview(): void {
        if (!this.previewModal) return;
        this.previewModal.classList.add('hidden');
        document.body.style.overflow = '';

        // Limpa o body para liberar memória (evita iframes rodando em background)
        if (this.previewBody) {
            this.previewBody.innerHTML = '';
        }
    }

    // ============================
    // Sidebar mobile
    // ============================
    protected toggleSidebar(): void {
        if (this.isOpen) {
            this.closeSidebar();
        } else {
            this.openSidebar();
        }
    }

    protected openSidebar(): void {
        this.isOpen = true;
        this.$el.classList.add('open');
        if (this.sidebarOverlay) {
            this.sidebarOverlay.classList.add('visible');
        }
    }

    protected closeSidebar(): void {
        this.isOpen = false;
        this.$el.classList.remove('open');
        if (this.sidebarOverlay) {
            this.sidebarOverlay.classList.remove('visible');
        }
    }

    // ============================
    // Helpers de estado visual
    // ============================

    /**
     * Exibe um spinner de carregamento centralizado (primeira carga da conversa).
     * Substitui o conteúdo atual só se a lista estiver vazia.
     */
    protected showLoadingSpinner(): void {
        if (!this.listEl) return;

        if (this.emptyEl) this.emptyEl.style.display = 'none';
        this.listEl.style.display = '';
        this.listEl.style.opacity = '1';
        this.listEl.innerHTML = '';

        const container = document.createElement('div');
        container.className = 'files-loading-container';
        container.innerHTML = `
            <div class="loading-spinner"></div>
            <span>Carregando arquivos...</span>
        `;
        this.listEl.appendChild(container);
    }

    /**
     * Renderiza itens otimistas no TOPO da lista usando dados do cache local (File objects).
     * Mostra nome real, tamanho e ícone por tipo — sem skeleton, sem esperar o servidor.
     * Marcados com [data-optimistic] para remoção posterior (sucesso ou falha).
     */
    protected showOptimisticFiles(files: {name: string; size: number; mime_type: string}[]): void {
        if (!this.listEl) return;

        if (this.emptyEl) this.emptyEl.style.display = 'none';
        this.listEl.style.display = '';
        this.listEl.style.opacity = '1';

        // Remove otimistas anteriores se existirem (envio duplicado rápido)
        this.clearOptimisticItems();

        // Cria item real para cada arquivo (com dados do browser, sem URL de download)
        for (const file of [...files].reverse()) {
            const optimisticFile: ChatFile = {
                message_id: 0,
                index: 0,
                name: file.name,
                mime_type: file.mime_type,
                size: file.size,
                created_at: new Date().toISOString(),
                url: '',
            };
            const item = this.createFileItem(optimisticFile);
            item.setAttribute('data-optimistic', '1');
            item.classList.add('file-item-pending');

            // Desabilita botões de ação (sem URL ainda)
            const actions = item.querySelector('.file-item-actions');
            if (actions) {
                (actions as HTMLElement).style.display = 'none';
            }

            item.style.opacity = '0';
            item.style.transition = 'opacity 0.25s ease';
            this.listEl.insertBefore(item, this.listEl.firstChild);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => { item.style.opacity = '1'; });
            });
        }
    }

    protected createSkeletonItem(): HTMLElement {
        const item = document.createElement('div');
        item.className = 'sidebar-file-item skeleton-loading';
        item.innerHTML = `
            <div class="file-item-icon file-icon-default"></div>
            <div class="file-item-content">
                <div class="skeleton-line"></div>
                <div class="skeleton-line-sm" style="margin-top:0.3rem"></div>
            </div>
        `;
        return item;
    }

    protected showEmpty(): void {
        if (this.listEl) {
            this.listEl.innerHTML = '';
            this.listEl.style.display = 'none';
        }
        if (this.emptyEl) {
            this.emptyEl.style.display = '';
        }
    }

    // ============================
    // Utilitários
    // ============================
    protected getFileIconInfo(mimeType: string): {cssClass: string; label: string} {
        if (mimeType.startsWith('image/')) {
            return {cssClass: 'file-icon-image', label: 'IMG'};
        }
        if (mimeType === 'application/pdf') {
            return {cssClass: 'file-icon-pdf', label: 'PDF'};
        }
        if (mimeType.startsWith('text/')) {
            return {cssClass: 'file-icon-text', label: 'TXT'};
        }
        return {cssClass: 'file-icon-default', label: 'ARQ'};
    }

    protected isPreviewable(mimeType: string): boolean {
        return mimeType.startsWith('image/')
            || mimeType === 'application/pdf'
            || mimeType.startsWith('text/');
    }

    protected formatSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
}
